import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { fetchCampaignMetadata } from '~/services/campaign';

const previousMonthDefaults = (): { from: string; to: string } => {
  const today = new Date();
  const firstOfThisMonth = new Date(today.getFullYear(), today.getMonth(), 1);
  const lastOfLastMonth = new Date(firstOfThisMonth.getTime() - 86400000);
  const firstOfLastMonth = new Date(lastOfLastMonth.getFullYear(), lastOfLastMonth.getMonth(), 1);
  const fmt = (d: Date) => d.toISOString().slice(0, 10);
  return { from: fmt(firstOfLastMonth), to: fmt(lastOfLastMonth) };
};

interface CscOption {
  id: number;
  full_name: string;
}

const CampaignOpenToDisbursableAccordionItem: React.FC = () => {
  const navigate = useNavigate();
  const defaults = previousMonthDefaults();
  const [fromDate, setFromDate] = useState(defaults.from);
  const [toDate, setToDate] = useState(defaults.to);
  const [csc, setCsc] = useState('');
  const [cscOptions, setCscOptions] = useState<CscOption[]>([]);

  useEffect(() => {
    fetchCampaignMetadata()
      .then((metadata) => {
        if (metadata?.patient_services_coordinators) {
          setCscOptions(metadata.patient_services_coordinators);
        }
      })
      .catch((error) => console.error('Error fetching CSCs:', error));
  }, []);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const params = new URLSearchParams();
    if (fromDate) params.set('from_date', fromDate);
    if (toDate) params.set('to_date', toDate);
    if (csc) params.set('csc', csc);
    navigate(`/reports/predefined/campaign-open-to-disbursable/results?${params.toString()}`);
  };

  return (
    <div className='flex h-full w-full flex-col rounded-sm border border-stroke bg-white px-7.5 py-5 shadow-default'>
      <h4 className='text-title-xsm font-bold sm:text-title-md'>
        Campaign Open to Disbursable
      </h4>
      <p className='mt-2 text-sm text-body'>
        How long each campaign took to receive its first donation and to become
        disbursable. Filtered by when the campaign opened; defaults to the previous
        calendar month. Covers every campaign that opened in the window, including
        campaigns that never had a campaign request.
      </p>
      <form onSubmit={handleSubmit} className='mt-4 flex flex-1 flex-col'>
        <div className='flex flex-col gap-4'>
          <div className='flex flex-col gap-4 sm:flex-row'>
            <div className='flex-1'>
              <label className='block text-[12px] font-bold mb-1'>Opened from</label>
              <input
                type='date'
                value={fromDate}
                onChange={(e) => setFromDate(e.target.value)}
                className='h-10 w-full rounded border border-gray-400 px-3 text-sm'
              />
            </div>
            <div className='flex-1'>
              <label className='block text-[12px] font-bold mb-1'>To</label>
              <input
                type='date'
                value={toDate}
                onChange={(e) => setToDate(e.target.value)}
                className='h-10 w-full rounded border border-gray-400 px-3 text-sm'
              />
            </div>
          </div>
          <div>
            <label className='block text-[12px] font-bold mb-1'>CSC</label>
            <select
              value={csc}
              onChange={(e) => setCsc(e.target.value)}
              className='h-10 w-full rounded border border-gray-400 px-3 text-sm'
            >
              <option value=''>All CSCs</option>
              {cscOptions.map((option) => (
                <option key={option.id} value={String(option.id)}>
                  {option.full_name}
                </option>
              ))}
            </select>
          </div>
        </div>
        <div className='mt-auto pt-6'>
          <button type='submit' className='rounded-lg bg-primary px-8 py-3 text-sm font-medium text-white'>
            Run report
          </button>
        </div>
      </form>
    </div>
  );
};

export default CampaignOpenToDisbursableAccordionItem;
